import { reducer as formReducer } from 'redux-form';
import { AnyAction, combineReducers } from 'redux';
import { LOGOUT_USER } from '../constants/actionConstants';
import { centerTicketPrintView } from './actions/ticketActions';
import { currentAppNameReducer } from './reducers/appReducer';
import {
  authReducer,
  newTokenRequest,
  minutesToIdleLogout,
  sessionId,
  userUrl,
  authTenantName,
  tenantId,
  token,
  refreshToken,
  hasTemporaryPassword,
  username,
  userFullName,
} from './reducers/authReducers';
import { citiesReducer } from './reducers/citiesReducer';
import { customersReducer } from './reducers/customersReducer';
import {
  dataTableDateRange,
  dataTableQueryParams,
  dataTableReducer,
  dataTableSearch,
  dataTableNeedsRefresh,
  combinedDataSources,
} from './reducers/dataTableReducers';
import directionsCache from './reducers/directionsCacheReducer';
import { currentDriverReducer, driversReducer, allDriversReducer } from './reducers/driverReducers';
import { flagReducerCombined, featureFlagObj } from './reducers/featureFlagReducers';
import { currentGeozone } from './reducers/geolocationReducers';
import {
  modalReducer,
  searchReducer,
  snackbarNotificationReducer,
  snackbarReducer,
  timeStampReducer,
} from './reducers/globalComponentsReducers';
import { haulersReducer } from './reducers/haulerReducers';
import { mapFilters } from './reducers/mapFilterReducers';
import {
  activePinMap,
  popupToggleMap,
  positionList,
  incomingPositionList,
  selectedMarker,
  activeMarkers,
  pinRadiusMiles,
} from './reducers/mapReducers';
import {
  currentTabReducer,
  inboxReplyMessageReducer,
  messageCenterReducer,
} from './reducers/messageCenterReducers';
import { messageList } from './reducers/messagingReducers';
import {
  trucksDropdownIsOpen,
  geotraxPage,
  guideDropdownIsOpen,
  driversDropdownIsOpen,
  openStateMap,
  sidebar,
  externalTrucksDropdownIsOpen,
} from './reducers/navReducers';
import { notebookOperatorsReducer, operatorNotesReducer } from './reducers/operatorNotesReducer';
import { ordersReducer } from './reducers/ordersReducer';
import {
  adminPermissionAccessReducer,
  assignedPermissionsReducer,
  permissionGroupsReducer,
  permissionsReducer,
  userPermissionReducer,
} from './reducers/permissionReducers';
import { plantList, currentPlant, incomingPlantList } from './reducers/plantReducers';
import { printerReducer } from './reducers/printersReducers';
import {
  primaryProductLine,
  routeHistoryProductLines,
  secondaryProductLine,
  secondaryProductLines,
  selectedProductLine,
} from './reducers/productLineReducers';
import { productsReducer } from './reducers/productsReducer';
import { projectsReducer, recentProjectsReducer } from './reducers/projectsReducer';
import {
  assignedRegionList,
  currentRegion,
  regionList,
  defaultRegionList,
  selectedRegion,
  userOverrides,
} from './reducers/regionReducers';
import {
  routeHistoryHoverSelection,
  routeHistoryList,
  selectedRouteHistoryList,
} from './reducers/routeHistoryReducers';
import {
  scaleReducer,
  receivedWeightReducer,
  scaleHubStatusReducer,
} from './reducers/scaleReducers';
import releaseDocsReducer from './reducers/staticResourceReducers';
import { messageSubscriptionReducer } from './reducers/subscriptionReducers';
import { telemetryLogger } from './reducers/telemetryReducer';
import { tenantsReducer } from './reducers/tenantReducers';
import {
  currentTicketsColumns,
  pastTicketEndDate,
  pastTicketList,
  pastTicketStartDate,
  selectedTicket,
  ticketsReducer,
  incomingTicketsReducer,
  currentTicketQualityFactors,
  ticketTimeline,
  ticketView,
  scaleTraxSearchFilters,
  ticketListFilters,
  isTicketPrintViewCenter,
  isOpenTicket,
  needToRefreshData,
} from './reducers/ticketReducers';
import { trucksReducer, allTrucksReducer } from './reducers/truckReducers';
import { usersReducer } from './reducers/userReducers';

// Define PayloadAction type for test files
export interface PayloadAction<T = any> {
  type: string;
  payload: T;
}

const appReducer = combineReducers({
  currentAppName: currentAppNameReducer,
  snackbar: snackbarReducer,
  snackbarNotification: snackbarNotificationReducer,
  messages: messageCenterReducer,
  messageList,
  replyMap: inboxReplyMessageReducer,
  auth: authReducer,
  positionList,
  incomingPositionList,
  assignedRegionList,
  regionList,
  currentRegion,
  selectedProductLine,
  currentTicketsColumns,
  driverList: driversReducer,
  allDrivers: allDriversReducer,
  allTrucks: allTrucksReducer,
  dataTableList: dataTableReducer,
  usersList: usersReducer,
  tenantsList: tenantsReducer,
  haulersList: haulersReducer,
  scalesList: scaleReducer,
  printerList: printerReducer,
  scaleHub: scaleHubStatusReducer,
  customersList: customersReducer,
  projectsList: projectsReducer,
  allProducts: productsReducer,
  citiesList: citiesReducer,
  trucksList: trucksReducer,
  ticketsList: ticketsReducer,
  incomingTicketsList: incomingTicketsReducer,
  currentTicketQualityFactors,
  permissionList: permissionsReducer,
  permissionGroupList: permissionGroupsReducer,
  assignedPermissionList: assignedPermissionsReducer,
  receivedWeight: receivedWeightReducer,
  ordersList: ordersReducer,
  pastTicketList,
  pastTicketStartDate,
  pastTicketEndDate,
  ticketTimeline,
  ticketListFilters,
  currentDriver: currentDriverReducer,
  openModal: modalReducer,
  searchReducer,
  popupToggleMap,
  selectedMarker,
  activeMarkers,
  directionsCache,
  selectedTicket,
  isOpenTicket,
  needToRefreshData,
  currentTab: currentTabReducer,
  ticketView,
  timeStamp: timeStampReducer,
  messageSubscription: messageSubscriptionReducer,
  form: formReducer,
  flags: flagReducerCombined,
  newTokenRequest,
  dataTableSearch,
  dataTableDateRange,
  dataTableQueryParams,
  dataTableNeedsRefresh,
  sidebar,
  plantList,
  incomingPlantList,
  currentPlant,
  trucksDropdownIsOpen,
  externalTrucksDropdownIsOpen,
  guideDropdownIsOpen,
  driversDropdownIsOpen,
  openStateMap,
  mapFilters,
  userPermission: userPermissionReducer,
  adminPermissionAccess: adminPermissionAccessReducer,
  releasenotes: releaseDocsReducer,
  minutesToIdleLogout,
  sessionId,
  userUrl,
  defaultRegionList,
  userOverrides,
  authTenantName,
  primaryProductLine,
  secondaryProductLine,
  secondaryProductLines,
  tenantId,
  token,
  refreshToken,
  hasTemporaryPassword,
  username,
  featureFlagObj,
  userFullName,
  recentProjects: recentProjectsReducer,
  operatorNotes: operatorNotesReducer,
  notebookOperators: notebookOperatorsReducer,
  scaleTraxSearchFilters,
  geotraxPage,
  currentGeozone,
  activePinMap,
  pinRadiusMiles,
  routeHistoryProductLines,
  routeHistoryList,
  selectedRouteHistoryList,
  routeHistoryHoverSelection,
  selectedRegion,
  centerTicketPrintView,
  isTicketPrintViewCenter,
  combinedDataSources,
  telemetryLogger,
});

type AppReducer = typeof appReducer;
export type ReduxState = ReturnType<AppReducer>;

export const rootReducer = (state?: ReduxState, action?: AnyAction) => {
  let newState = state;
  if (action?.type === LOGOUT_USER) {
    newState = undefined;
  }
  const result = appReducer(newState, action!);
  return result;
};
